import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    Typography,
} from '@material-ui/core';
import { Close as CloseIcon, CloudUpload as CloudUploadIcon } from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';

import { useStyles } from './DeviceImportDialog.styles';
import { ACCEPTED_FILE_TYPES } from './DeviceImportDialog.constants';
import { useDeviceImport } from '../hooks/UseDeviceImport';
import CsvFormatHelp from './CsvFormatHelp';

/**
 * Monitored Device Import Dialog.
 * Handles CSV file upload for bulk device import with status polling.
 *
 * Flow: File Selection → Validation → Upload → Poll Status → Display Result
 *
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {Function} props.setOpen - Closes the dialog
 * @param {Function} [props.onImportComplete] - Callback to trigger list refresh after successful import
 */
export default function DeviceImportDialog({ open, setOpen, onImportComplete }) {
    const classes = useStyles();

    const { state, refs, handlers, computed } = useDeviceImport({ onImportComplete, setOpen });

    const { file, importStatus, importMessage, importErrors, uploadFailed, uploadFailMessage } = state;

    const { fileInputRef } = refs;

    const { handleFileSelect, handleUpload, handleClose, getStatusMessageClass } = handlers;

    const {
        isSelectPhase,
        isUploadingPhase,
        isResultPhase,
        uploadDisabled,
        errorMessage: clientErrorMessage,
    } = computed;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth disableEscapeKeyDown={isUploadingPhase}>
            <DialogTitle disableTypography className={classes.dialogTitle}>
                <Typography variant="h6">
                    <Trans>Import File</Trans>
                </Typography>
                <IconButton
                    size="small"
                    onClick={handleClose}
                    disabled={isUploadingPhase}
                    aria-label={i18n._(t`Close dialog`)}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {/* Client-side error banner */}
                {clientErrorMessage && (
                    <Box mb={2}>
                        <Alert severity="error">{clientErrorMessage}</Alert>
                    </Box>
                )}

                {/* Upload failure banner */}
                {uploadFailed && (
                    <Box mb={2}>
                        <Alert severity="error">{uploadFailMessage}</Alert>
                    </Box>
                )}

                {/* File selection UI */}
                {isSelectPhase && (
                    <>
                        <Box className={classes.fileRow}>
                            <Button
                                variant="contained"
                                color="primary"
                                component="label"
                                startIcon={<CloudUploadIcon />}
                            >
                                {i18n._(t`BROWSE`)}
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={ACCEPTED_FILE_TYPES}
                                    style={{ display: 'none' }}
                                    onChange={handleFileSelect}
                                />
                            </Button>
                            <Box className={classes.fileName}>{file?.name || ''}</Box>
                        </Box>
                        <CsvFormatHelp classes={classes} />
                    </>
                )}

                {/* Uploading / polling phase */}
                {isUploadingPhase && (
                    <Box className={classes.feedbackArea}>
                        <LinearProgress />
                        <Box mt={1}>
                            <Typography variant="body2">{importMessage}</Typography>
                        </Box>
                    </Box>
                )}

                {/* Result phase */}
                {isResultPhase && (
                    <Box className={classes.feedbackArea}>
                        <Box mb={1}>
                            <Typography variant="body1" className={getStatusMessageClass(importStatus, classes)}>
                                {importMessage}
                            </Typography>
                        </Box>
                        {importErrors.length > 0 && (
                            <Box>
                                {importErrors.map((error) => (
                                    <Typography key={error} variant="body2" className={classes.errorItem}>
                                        {error}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>

            <DialogActions>
                {isSelectPhase && (
                    <Button color="primary" variant="contained" onClick={handleUpload} disabled={uploadDisabled}>
                        <Trans>Upload</Trans>
                    </Button>
                )}
                <Button onClick={handleClose} disabled={isUploadingPhase}>
                    <Trans>Close</Trans>
                </Button>
            </DialogActions>
        </Dialog>
    );
}
